import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  History, RefreshCw, Plus, Edit, Trash2, GitBranch, Package, FileCheck, Printer, FileSpreadsheet,
  CheckCircle2, User, Calendar, Info,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { apqpService } from '../../services/api';

const EVENT_META = {
  project_created:         { label: 'Progetto creato',       icon: Plus,         color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  project_updated:         { label: 'Progetto modificato',   icon: Edit,         color: 'bg-blue-100 text-blue-800 border-blue-300' },
  project_deleted:         { label: 'Progetto eliminato',    icon: Trash2,       color: 'bg-red-100 text-red-800 border-red-300' },
  project_status_changed:  { label: 'Cambio stato progetto', icon: Info,         color: 'bg-purple-100 text-purple-800 border-purple-300' },
  task_created:            { label: 'Attività creata',       icon: Plus,         color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  task_updated:            { label: 'Attività modificata',   icon: Edit,         color: 'bg-blue-100 text-blue-800 border-blue-300' },
  task_deleted:            { label: 'Attività eliminata',    icon: Trash2,       color: 'bg-red-100 text-red-800 border-red-300' },
  task_date_changed:       { label: 'Cambio date',           icon: Calendar,     color: 'bg-amber-100 text-amber-800 border-amber-300' },
  task_status_changed:     { label: 'Cambio stato attività', icon: Info,         color: 'bg-sky-100 text-sky-800 border-sky-300' },
  task_completed:          { label: 'Attività completata',   icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  baseline_created:        { label: 'Baseline salvata',      icon: GitBranch,    color: 'bg-teal-100 text-teal-800 border-teal-300' },
  revision_created:        { label: 'Revisione registrata',  icon: History,      color: 'bg-slate-200 text-slate-800 border-slate-400' },
  sampling_created:        { label: 'Campionatura creata',   icon: Package,      color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  sampling_updated:        { label: 'Campionatura modificata', icon: Package,    color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  document_created:        { label: 'Documento aggiunto',    icon: FileCheck,    color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  document_updated:        { label: 'Documento aggiornato',  icon: FileCheck,    color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  document_status_changed: { label: 'Cambio stato doc',      icon: FileCheck,    color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  export_pdf:              { label: 'Export PDF',            icon: Printer,      color: 'bg-slate-100 text-slate-700 border-slate-300' },
  export_excel:            { label: 'Export Excel',          icon: FileSpreadsheet, color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const ALL_EVENT_TYPES = Object.keys(EVENT_META);

const fmt = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'medium' }) : '—';

function renderDetails(e) {
  const t = e.event_type;
  const d = e.details || {};
  if (t === 'project_status_changed') return `${d.before || '—'} → ${d.after || '—'}`;
  if (t === 'task_status_changed') return `${d.before || '—'} → ${d.after || '—'}`;
  if (t === 'task_date_changed') {
    const chs = d.changes || {};
    return Object.entries(chs).map(([k, v]) => `${k}: ${v.before || '—'} → ${v.after || '—'}`).join(' · ');
  }
  if (t === 'project_updated') {
    const chs = d.changes || {};
    const keys = Object.keys(chs);
    if (!keys.length) return '';
    return keys.map(k => `${k}: ${chs[k].before || '—'} → ${chs[k].after || '—'}`).join(' · ');
  }
  if (t === 'baseline_created') return `${d.reason || ''} · ${d.task_count || 0} attività`;
  if (t === 'revision_created') return `${d.reason || ''} · ${d.changes_count || 0} modifiche`;
  if (t === 'export_pdf') return `variant=${d.variant || 'internal'}`;
  if (t === 'task_completed') return d.actual_end ? `Completata il ${d.actual_end}` : 'Completata';
  return '';
}

export default function ApqpAuditTab({ projectId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apqpService.audit(projectId, 500);
      setEvents(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento storico');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = events;
    if (filterType) list = list.filter(e => e.event_type === filterType);
    if (q.trim()) {
      const rx = q.trim().toLowerCase();
      list = list.filter(e =>
        (e.entity_name || '').toLowerCase().includes(rx) ||
        (e.user_email || '').toLowerCase().includes(rx) ||
        renderDetails(e).toLowerCase().includes(rx)
      );
    }
    return list;
  }, [events, filterType, q]);

  const counts = useMemo(() => {
    const c = {};
    events.forEach(e => { c[e.event_type] = (c[e.event_type] || 0) + 1; });
    return c;
  }, [events]);

  const activeEventTypes = useMemo(
    () => ALL_EVENT_TYPES.filter(k => counts[k]),
    [counts],
  );

  return (
    <div className="space-y-3" data-testid="apqp-audit-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-800">Storico modifiche</h3>
          <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]">{events.length}</Badge>
          <span className="text-xs text-slate-400">Log immutabile · pronto per audit IATF 16949</span>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-audit-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Cerca in nome/utente/dettagli…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="h-8 text-xs max-w-xs"
          data-testid="audit-search"
        />
        <div className="flex flex-wrap gap-1">
          <Badge
            onClick={() => setFilterType('')}
            className={`cursor-pointer border text-[10px] ${!filterType ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300'}`}
          >
            Tutti ({events.length})
          </Badge>
          {activeEventTypes.map(k => {
            const meta = EVENT_META[k];
            const active = filterType === k;
            return (
              <Badge
                key={k}
                onClick={() => setFilterType(active ? '' : k)}
                className={`cursor-pointer border text-[10px] ${active ? 'ring-2 ring-slate-900 ' + meta.color : meta.color}`}
              >
                {meta.label} ({counts[k]})
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <Card>
        <CardContent className="p-4">
          {loading && events.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Caricamento…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="audit-empty">
              {events.length === 0 ? 'Nessun evento registrato per questo progetto.' : 'Nessun evento corrisponde al filtro.'}
            </p>
          ) : (
            <ol className="relative border-l-2 border-slate-100 ml-3 space-y-3" data-testid="audit-list">
              {filtered.map(e => {
                const meta = EVENT_META[e.event_type] || { label: e.event_type, icon: Info, color: 'bg-slate-100 text-slate-700 border-slate-300' };
                const Icon = meta.icon;
                const details = renderDetails(e);
                return (
                  <li key={e.id} className="ml-4" data-testid={`audit-event-${e.id}`}>
                    <div className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white ${meta.color.split(' ')[0]}`} />
                    <div className="flex flex-wrap items-start gap-2">
                      <Badge className={`border text-[10px] ${meta.color}`}>
                        <Icon className="w-3 h-3 mr-1 inline" />{meta.label}
                      </Badge>
                      <span className="text-xs text-slate-500 font-mono">{fmt(e.created_at)}</span>
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                        <User className="w-3 h-3" /> {e.user_email || '—'}
                      </span>
                      {e.entity_name && (
                        <span className="text-xs font-medium text-slate-800 truncate max-w-md">{e.entity_name}</span>
                      )}
                    </div>
                    {details && (
                      <div className="text-xs text-slate-600 mt-0.5 pl-1 font-mono truncate max-w-full">{details}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
